import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import Annotation from "chartjs-plugin-annotation";
import { useDispatch, useSelector } from "react-redux";

import { CompanyLoadTooltip } from "./CompanyLoadTooltip";
import { RateHistoryTooltip } from "./RateHistoryTooltip";
import { ChartLegend } from "./ChartLegend";
import { NoData, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { ErrorComponent } from "@/components/common/errors";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { useQuery } from "@/utils/query";
import { getMonthName } from "@/utils/date";
import { clearRateHistoryState, getRateHistory } from "@/state/rateHistorySlice";
import { cn } from "@/utils/css";
import { calculateMaxValue, calculateMinValue } from "@/utils/graph";
import {
    generateMonthArray,
    formatCurrency,
    calculateMinTickValue,
    calculateMaxTickValue,
    processCompanyLoadsScatterData,
    createChartDatasets,
    createChartOptions,
} from "./predictionLineChartUtils";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, Filler, Annotation);

// Temporarily disabled; flip to true to bring the 12-month forecast back.
const FORECAST_ENABLED = false;

export const PredictionLineChart = () => {
    const [showCompanyReports, setShowCompanyReports] = useState(true);
    const [showForecast, setShowForecast] = useState(true);
    const dispatch = useDispatch();
    const {
        data: { rates: rateData, forecast, companyLoads },
        loading,
        error,
    } = useSelector((state) => state.rateHistory);
    const isDarkTheme = useIsDarkTheme();
    const { pickupZip, deliveryZip, trailerType } = useQuery();
    const [tooltipData, setTooltipData] = useState({
        x: 0,
        y: 0,
        visible: false,
        maxValue: null,
        minValue: null,
        medianValue: null,
        isCompanyLoad: false,
        companyLoadData: null,
    });

    useEffect(() => {
        dispatch(getRateHistory({ pickupZip, deliveryZip, trailerType }));
    }, [pickupZip, deliveryZip, trailerType]);

    // Data transformations
    const flatData = useMemo(() => (rateData ? rateData.map((r) => (r.avgPrice ? parseInt(r.avgPrice) : null)) : []), [rateData]);

    const minRangeData = useMemo(() => (rateData ? rateData.map((r) => r.minPrice || null) : []), [rateData]);
    const maxRangeData = useMemo(() => (rateData ? rateData.map((r) => r.maxPrice || null) : []), [rateData]);

    const companyLoadsScatterData = useMemo(() => processCompanyLoadsScatterData(companyLoads, rateData), [companyLoads, rateData]);

    const defaultMonths = generateMonthArray();
    const months = useMemo(() => (rateData.length ? rateData.map((d) => getMonthName(d.month)) : defaultMonths), [rateData]);

    // Forecast entries overlaid on the same month axis: the forecast covers the 12 months following
    // the last historical month, so aligned by calendar month it spans axis indices 1..12 while the
    // current month-to-date (last history entry) anchors the line at index 0.
    const forecastRates = useMemo(() => {
        if (!FORECAST_ENABLED || !rateData?.length || !forecast?.length) return [];
        const overlay = new Array(months.length).fill(null);
        overlay[0] = rateData[rateData.length - 1];
        forecast.forEach((f, i) => {
            if (i + 1 < overlay.length) overlay[i + 1] = f;
        });
        return overlay;
    }, [rateData, forecast, months]);

    const forecastLineData = useMemo(() => forecastRates.map((f) => (f && f.avgPrice ? parseInt(f.avgPrice) : null)), [forecastRates]);
    const forecastMinRangeData = useMemo(() => forecastRates.map((f) => (f ? f.minPrice || null : null)), [forecastRates]);
    const forecastMaxRangeData = useMemo(() => forecastRates.map((f) => (f ? f.maxPrice || null : null)), [forecastRates]);

    // Y-axis calculations
    const allValues = useMemo(() => {
        const chartValues = flatData.filter((v) => v !== null);
        const companyValues = showCompanyReports ? companyLoadsScatterData.map((d) => d.y) : [];
        const minValues = minRangeData.filter((v) => v !== null);
        const maxValues = maxRangeData.filter((v) => v !== null);
        const forecastValues = showForecast
            ? [...forecastLineData, ...forecastMinRangeData, ...forecastMaxRangeData].filter((v) => v !== null)
            : [];
        return [...chartValues, ...companyValues, ...minValues, ...maxValues, ...forecastValues];
    }, [
        flatData,
        companyLoadsScatterData,
        showCompanyReports,
        minRangeData,
        maxRangeData,
        showForecast,
        forecastLineData,
        forecastMinRangeData,
        forecastMaxRangeData,
    ]);

    const maxValue = useMemo(() => calculateMaxValue(allValues), [allValues]);
    const minValue = useMemo(() => calculateMinValue(allValues), [allValues]);
    const stepSize = 100;
    const minTickValue = useMemo(() => calculateMinTickValue(minValue, stepSize), [minValue, stepSize]);
    const maxTickValue = useMemo(() => calculateMaxTickValue(maxValue, stepSize, minTickValue), [maxValue, stepSize, minTickValue]);

    // Tooltip handler
    const externalTooltipHandler = useCallback(
        (context) => {
            const { chart, tooltip } = context;
            if (!tooltip || tooltip.opacity === 0) {
                setTooltipData((prev) => (prev.visible ? { ...prev, visible: false } : prev));
                return;
            }

            const dataPoint = tooltip.dataPoints?.[0];
            const datasetLabel = dataPoint?.dataset?.label;

            if (["minRange", "maxRange", "forecastMinRange", "forecastMaxRange"].includes(datasetLabel)) {
                setTooltipData((prev) => (prev.visible ? { ...prev, visible: false } : prev));
                return;
            }

            const isCompanyLoad = datasetLabel === "companyLoads";
            const companyLoadData = isCompanyLoad && dataPoint?.raw ? dataPoint.raw : null;
            const hasBroker = isCompanyLoad && Boolean(companyLoadData?.broker);

            const tooltipWidth = 180;
            const tooltipHeight = isCompanyLoad && hasBroker ? 127 : 100;
            const chartWidth = chart.width;
            const chartHeight = chart.height;
            const offset = 9;

            let x = tooltip.caretX + offset;
            let y = tooltip.caretY - tooltipHeight / 2;

            // Shift left if tooltip would overflow right edge
            if (x + tooltipWidth > chartWidth) {
                x = tooltip.caretX - tooltipWidth - offset;
            }
            // Ensure tooltip doesn't go past left edge
            if (x < 0) {
                x = 0;
            }
            // Adjust vertical position if needed
            if (y + tooltipHeight > chartHeight) {
                y = chartHeight - tooltipHeight;
            }
            if (y < 0) {
                y = 0;
            }

            const isForecast = datasetLabel === "forecast";
            const monthIndex = isForecast ? dataPoint?.raw?.x : dataPoint?.dataIndex;
            const monthSource = isForecast ? forecastRates : rateData;
            const monthData = monthIndex != null && monthSource?.[monthIndex] ? monthSource[monthIndex] : null;

            const newData = {
                x,
                y,
                visible: true,
                maxValue: formatCurrency(monthData?.maxPrice, true),
                minValue: formatCurrency(monthData?.minPrice, true),
                medianValue: formatCurrency(monthData?.avgPrice, true),
                isCompanyLoad,
                companyLoadData,
            };

            setTooltipData((prev) => {
                if (
                    prev.x !== newData.x ||
                    prev.y !== newData.y ||
                    prev.medianValue !== newData.medianValue ||
                    prev.visible !== newData.visible ||
                    prev.isCompanyLoad !== newData.isCompanyLoad
                ) {
                    return newData;
                }
                return prev;
            });
        },
        [rateData, forecastRates],
    );

    // Chart configuration
    const chartData = useMemo(
        () => ({
            labels: months,
            datasets: createChartDatasets({
                maxValue,
                minRangeData,
                maxRangeData,
                flatData,
                showCompanyReports,
                companyLoadsScatterData,
                showForecast,
                forecastData: forecastLineData,
                forecastMinRangeData,
                forecastMaxRangeData,
                isDarkTheme,
            }),
        }),
        [
            maxValue,
            flatData,
            showCompanyReports,
            companyLoadsScatterData,
            showForecast,
            forecastLineData,
            forecastMinRangeData,
            forecastMaxRangeData,
            months,
            isDarkTheme,
            minRangeData,
            maxRangeData,
        ],
    );

    const options = useMemo(
        () =>
            createChartOptions({
                maxValue,
                minTickValue,
                maxTickValue,
                stepSize,
                months,
                isDarkTheme,
                externalTooltipHandler,
            }),
        [maxValue, minTickValue, maxTickValue, stepSize, months, isDarkTheme, externalTooltipHandler],
    );

    useEffect(() => {
        return () => {
            dispatch(clearRateHistoryState());
        };
    }, []);

    const handleToggleCompanyReports = useCallback(() => {
        setShowCompanyReports((prev) => !prev);
    }, []);

    const handleToggleForecast = useCallback(() => {
        setShowForecast((prev) => !prev);
    }, []);

    return (
        <div className="flex flex-col pt-4 border-t border-cm-p-outline-variation lg:pt-0 lg:border-0 mt-0 sm:mt-4 lg:mt-0">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pb-3">
                {/* Title — always first, row 1 left */}
                <div className="order-1 text-cm-p-text-primary text-base/[22px] font-medium flex items-center">
                    <TranslatedText>Rate data</TranslatedText>
                    <div className="ml-2">
                        <InfoButton text="Rate history provides insights into how rates have changed over time for this lane." />
                    </div>
                </div>

                {/* Legend — same row as title, wraps under it when too narrow */}
                <div className="order-2">
                    <ChartLegend
                        showCompanyReports={showCompanyReports}
                        onToggleCompanyReports={handleToggleCompanyReports}
                        hasForecast={forecastRates.length > 0}
                        showForecast={showForecast}
                        onToggleForecast={handleToggleForecast}
                    />
                </div>
            </div>
            <div className="relative flex-1 w-full h-[267px] min-h-[267px] min-[1490px]:h-[295px] min-[1490px]:min-h-[295px]">
                <div className="h-[267px] min-h-[267px] w-full min-[1490px]:h-[295px] min-[1490px]:min-h-[295px]">
                    <Chart type="line" data={chartData} options={options} />
                </div>
                {tooltipData.isCompanyLoad && tooltipData.companyLoadData ? (
                    <CompanyLoadTooltip {...tooltipData} />
                ) : (
                    <RateHistoryTooltip {...tooltipData} />
                )}
                {loading || error || (flatData.length && maxValue !== 0) ? null : (
                    <NoData className="absolute top-20 text-center w-full pl-12 pr-4" />
                )}
                <ErrorComponent
                    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0", {
                        hidden: !error,
                    })}
                />
            </div>
        </div>
    );
};
